import { inject, Injectable } from "@angular/core";
import { CommonProcessTreeQueryComplexInput, CommonProcessTreeSortInput, ComparatorOperators, CreateScenario, CreateScenarioGQL, CreateScenarioMutation, DeleteScenariosGQL, GetCptBySnapshotGQL, GetCptBySnapshotQuery, GetLegalEntitiesBySnapshotGQL, GetLegalEntitiesBySnapshotQuery, GetRisksGQL, GetRisksQuery, GetScenariosGQL, GetScenariosQuery, GetSnapshotsGQL, GetSnapshotsQuery, OperatorInput, OrderTypeInput, RiskParameterColumns, RiskParameterQueryComplexInput, RiskParameterSimpleQueryInput, RiskParameterSortInput, ScenarioColumns, ScenarioQueryComplexInput, ScenarioSimpleQueryInput, ScenarioSortInput, ScenarioType, UpdateScenario, UpdateScenarioGQL, UpdateScenarioMutation, UpdateScenariosLegalEntityGQL, UpdateScenariosLegalEntityMutation, UpdateScenariosProcessGQL, UpdateScenariosProcessMutation, UpdateScenariosRiskGQL, UpdateScenariosRiskMutation, DeleteScenariosMutation, LaunchScenarioGQL, LaunchScenarioMutation, GetScenariosHistoryGQL, GetScenariosHistoryQuery, UpdateScenarioNameOrVersionGQL, UpdateScenarioNameOrVersionMutation, ExportScenarioGQL, ExportScenarioMutation, PagingInput } from "../../../graphql/generated";
import { map, Observable } from "rxjs";
import { convertLazyLoadToComplexWithSortAndPage } from "./legal-entities.service";
import { convertLazyLoadToComplexWithoutPageCptTree } from "./cpt.service";

@Injectable({
    providedIn: 'root'
})
export class ScenarioConfigurationService {
    private readonly GetScenariosGQL: GetScenariosGQL = inject(GetScenariosGQL);
    private readonly GetSnapshotsGQL: GetSnapshotsGQL = inject(GetSnapshotsGQL);
    private readonly CreateScenarioGQL: CreateScenarioGQL = inject(CreateScenarioGQL);
    private readonly UpdateScenarioGql: UpdateScenarioGQL = inject(UpdateScenarioGQL);
    private readonly GetLegalEntitiesBySnapshotGQL: GetLegalEntitiesBySnapshotGQL = inject(GetLegalEntitiesBySnapshotGQL);
    private readonly GetCptBySnapshotGQL: GetCptBySnapshotGQL = inject(GetCptBySnapshotGQL);
    private readonly GetRisksGQL: GetRisksGQL = inject(GetRisksGQL);
    private readonly DeleteScenariosGQL: DeleteScenariosGQL = inject(DeleteScenariosGQL);
    private readonly UpdateScenariosLegalEntityGQL: UpdateScenariosLegalEntityGQL = inject(UpdateScenariosLegalEntityGQL);
    private readonly UpdateScenariosProcessGQL: UpdateScenariosProcessGQL = inject(UpdateScenariosProcessGQL);
    private readonly UpdateScenariosRiskGQL: UpdateScenariosRiskGQL = inject(UpdateScenariosRiskGQL);
    private readonly LaunchScenarioGQL: LaunchScenarioGQL = inject(LaunchScenarioGQL);
    private readonly GetScenariosHistoryGQL: GetScenariosHistoryGQL = inject(GetScenariosHistoryGQL);
    private readonly UpdateScenarioNameOrVersionGQL: UpdateScenarioNameOrVersionGQL = inject(UpdateScenarioNameOrVersionGQL);
    private readonly ExportScenarioGQL: ExportScenarioGQL = inject(ExportScenarioGQL);

    getScenarios(type: ScenarioType | null, parameters: any): Observable<GetScenariosQuery['scenariosByType']> {
        const param = convertLazyLoadToComplexWithoutPageScenario(parameters);
        return this.GetScenariosGQL.fetch({ type, query: param.query, sort: param.sort, page: param.page }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.scenariosByType))
    }

    getSnapshots(): Observable<GetSnapshotsQuery['snapshots']> {
        return this.GetSnapshotsGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.snapshots))
    }

    createScenario(create: CreateScenario): Observable<CreateScenarioMutation['createScenario']> {
        return this.CreateScenarioGQL.mutate({ create }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createScenario))
    }

    updateScenario(update: UpdateScenario): Observable<UpdateScenarioMutation['updateScenario']> {
        return this.UpdateScenarioGql.mutate({ update }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateScenario))
    }

    getLegalEntitiesBySnapshot(snapshotId: string, parameters: any): Observable<GetLegalEntitiesBySnapshotQuery['legalEntitiesByActiveCampaignInSnapshot']> {
        const param = convertLazyLoadToComplexWithSortAndPage(parameters);
        return this.GetLegalEntitiesBySnapshotGQL.fetch({ snapshotId, query: param.query, sort: param.sort }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.legalEntitiesByActiveCampaignInSnapshot))
    }

    getCptBySnapshot(snapshotId: string, parameters: any, legalEntityIds: string[]): Observable<GetCptBySnapshotQuery['commonProcessTreesByActiveCampaignInSnapshot']> {
        const param = convertLazyLoadToComplexWithoutPageCptTree(parameters);
        return this.GetCptBySnapshotGQL.fetch({ snapshotId, query: param.query as CommonProcessTreeQueryComplexInput, sort: param.sort as CommonProcessTreeSortInput, legalEntityIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.commonProcessTreesByActiveCampaignInSnapshot))
    }

    getRisks(parameters: any): Observable<GetRisksQuery['riskParametersFiltered']> {
        const param = convertLazyLoadToComplexWithoutPageRisks(parameters);
        return this.GetRisksGQL.fetch({ query: param.query, sort: param.sort }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskParametersFiltered))
    }

    deleteScenarios(ids: string[]): Observable<DeleteScenariosMutation['deleteScenarios']> {
        return this.DeleteScenariosGQL.mutate({ ids }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.deleteScenarios))
    }

    updateScenarioLegalEntities(ids: string[], legalEntityIds: string[]): Observable<UpdateScenariosLegalEntityMutation['updateScenariosLegalEntity']> {
        return this.UpdateScenariosLegalEntityGQL.mutate({ ids, legalEntityIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateScenariosLegalEntity))
    }

    updateScenarioProcess(ids: string[], processIds: string[]): Observable<UpdateScenariosProcessMutation['updateScenariosProcess']> {
        return this.UpdateScenariosProcessGQL.mutate({ ids, processIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateScenariosProcess))
    }

    updateScenarioRisk(ids: string[], riskIds: string[]): Observable<UpdateScenariosRiskMutation['updateScenariosRisk']> {
        return this.UpdateScenariosRiskGQL.mutate({ ids, riskIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateScenariosRisk))
    }

    launchScenario(id: string): Observable<LaunchScenarioMutation['launchScenario']> {
        return this.LaunchScenarioGQL.mutate({ id }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.launchScenario))
    }

    getScenarioHistory(type: ScenarioType | null, parameters: any): Observable<GetScenariosHistoryQuery['scenariosHistoryByType']> {
        const param = convertLazyLoadToComplexWithoutPageScenarioHistory(parameters);
        return this.GetScenariosHistoryGQL.fetch({ query: param.query, sort: param.sort, type, page: param.page }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.scenariosHistoryByType))
    }

    updateScenarioNameOrVersion(name: string | null, scenarioIds: string[], snapshotId: string | null): Observable<UpdateScenarioNameOrVersionMutation['updateManyScenario']> {
        return this.UpdateScenarioNameOrVersionGQL.mutate({ name, scenarioIds, snapshotId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateManyScenario))
    }

    //     exportScenario(scenarioId: string, valueChain: string[], timeZone: Date): Observable<any> {
    //   return this.ExportScenarioGQL.mutate(
    //     {
    //       scenarioId,
    //       valueChain,
    //       timeZone: ""  // Puoi personalizzare se serve
    //     },
    //     { fetchPolicy: 'no-cache' }
    //   ).pipe(map(res => res.data?.downloadScenarioHeatMap));
    // }

    downaloadHeatmaps(
        scenarioId: string,
        valueChain: string[],
        timeZone: string
    ): Observable<ExportScenarioMutation['downloadScenarioHeatMap']> {
        return this.ExportScenarioGQL.mutate(
            { scenarioId, valueChain, timeZone },
            { fetchPolicy: 'no-cache' }).pipe(
                map(res => (res.data?.downloadScenarioHeatMap)
                ))
    }



}

export function convertLazyLoadToComplexWithoutPageRisks(parameters: any): { query: RiskParameterQueryComplexInput, sort: RiskParameterSortInput } {
    const query: RiskParameterSimpleQueryInput[] = [];

    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                // console.log("value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_RISKS[field as keyof typeof ENUM_RISKS]).forEach(key => {

                        let filteredValue: any[] = [];
                        filteredValue = values
                        query.push({
                            field: key as RiskParameterColumns,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: filteredValue
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_RISKS?.[parameters?.order?.sortField as keyof typeof ENUM_RISKS]?.[0] as RiskParameterColumns || RiskParameterColumns.RiskName,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        }
    }
}

export const ENUM_RISKS = {
    parameterName: [RiskParameterColumns.RiskName],
    parameterCategory: [RiskParameterColumns.RiskCategory],
    parameterDescription: [RiskParameterColumns.RiskDescription],
    parameterStatus: [RiskParameterColumns.RiskStatus]
}

export function convertLazyLoadToComplexWithoutPageScenario(parameters: any): { query: ScenarioQueryComplexInput, sort: ScenarioSortInput, page: PagingInput } {
    const query: ScenarioSimpleQueryInput[] = [];

    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                // console.log("value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_SCENARIO[field as keyof typeof ENUM_SCENARIO]).forEach(key => {

                        let filteredValue: any[] = [];
                        filteredValue = values
                        query.push({
                            field: key as ScenarioColumns,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: filteredValue
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_SCENARIO?.[parameters?.order?.sortField as keyof typeof ENUM_SCENARIO]?.[0] as ScenarioColumns ?? ScenarioColumns.ScenarioName,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 20
        }
    }
}

export const ENUM_SCENARIO = {
    name: [ScenarioColumns.ScenarioName],
    lastModifiedAt: [ScenarioColumns.ScenarioLastModifiedAt],
    type: [ScenarioColumns.ScenarioType],
    status: [ScenarioColumns.ScenarioStatus],
    snapshot: [ScenarioColumns.SnapshotName],
    createdAt: [ScenarioColumns.ScenarioCreatedAt],
    createdBy: [ScenarioColumns.ScenarioCreatedBy],
    lastLaunchDate: [ScenarioColumns.ScenarioLastLaunchDate],
    // completedAt: [ScenarioColumns.],
    launchedAt: [ScenarioColumns.ScenarioLastLaunchDate]
}

export function convertLazyLoadToComplexWithoutPageScenarioHistory(parameters: any): { query: ScenarioQueryComplexInput, sort: ScenarioSortInput, page: PagingInput } {
    const query: ScenarioSimpleQueryInput[] = [];

    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                // console.log("value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_SCENARIO_HISTORY[field as keyof typeof ENUM_SCENARIO_HISTORY]).forEach(key => {

                        let filteredValue: any[] = [];
                        filteredValue = values
                        query.push({
                            field: key as ScenarioColumns,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: filteredValue
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_SCENARIO_HISTORY?.[parameters?.order?.sortField as keyof typeof ENUM_SCENARIO_HISTORY]?.[0] as ScenarioColumns || ScenarioColumns.ScenarioName,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 20
        }
    }
}

export const ENUM_SCENARIO_HISTORY = {
    name: [ScenarioColumns.ScenarioName],
    lastModifiedAt: [ScenarioColumns.ScenarioTaskLastModifiedAt],
    type: [ScenarioColumns.ScenarioType],
    status: [ScenarioColumns.ScenarioTaskStatus],
    snapshot: [ScenarioColumns.SnapshotName],
    createdAt: [ScenarioColumns.ScenarioCreatedAt],
    createdBy: [ScenarioColumns.ScenarioTaskCreatedBy],
    completedAt: [ScenarioColumns.ScenarioTaskCompletedAt],
    launchedAt: [ScenarioColumns.ScenarioTaskCreatedAt],
    snapshotCreatedAt: [ScenarioColumns.SnapshotCreatedAt],
    resultDataOf: [ScenarioColumns.ScenarioResultDataAsOf]
}
